import { router } from '@inertiajs/react'
import { Clock } from 'lucide-react'
import AppLayout from '@/layouts/AppLayout'
import AuditTimeline from '@/components/AuditTimeline'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import Pagination from '@/components/Pagination'
import type { AuditLog, PaginationMeta } from '@/types'

interface Props {
  audit_logs: AuditLog[]
  filters: { type: string | null; sort: string }
  pagination: PaginationMeta
}

export default function HistoryIndex({ audit_logs, filters, pagination }: Props) {
  function handleFilterChange(value: string) {
    router.get(
      '/history',
      { type: value === 'all' ? null : value, sort: filters.sort },
      { preserveState: true, preserveScroll: true }
    )
  }

  function handleSortChange(value: string) {
    router.get(
      '/history',
      { type: filters.type, sort: value },
      { preserveState: true, preserveScroll: true }
    )
  }

  return (
    <AppLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Changes</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Activity log of all changes
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="border-border/50 mb-6">
        <CardContent className="flex flex-wrap items-center gap-4 pt-6">
          <span className="text-sm font-medium text-muted-foreground">Filter by type:</span>
          <Select
            value={filters.type || 'all'}
            onValueChange={handleFilterChange}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="PurchaseOrder">Purchase Orders</SelectItem>
              <SelectItem value="Payment">Payments</SelectItem>
              <SelectItem value="Adjustment">Adjustments</SelectItem>
            </SelectContent>
          </Select>

          <span className="text-sm font-medium text-muted-foreground">Sort by:</span>
          <Select value={filters.sort} onValueChange={handleSortChange}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">Date created</SelectItem>
              <SelectItem value="record_date">Record date</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="size-4 text-muted-foreground" />
            Activity Feed
          </CardTitle>
        </CardHeader>
        <CardContent>
          <AuditTimeline logs={audit_logs} />
        </CardContent>
      </Card>

      <Pagination
        pagination={pagination}
        baseUrl="/history"
        params={{ type: filters.type, sort: filters.sort }}
      />
    </AppLayout>
  )
}
